import { useState, type MouseEvent } from "react";
import { useTranslation } from "react-i18next";
import Stack from "@mui/material/Stack";
import Chip from "@mui/material/Chip";
import Link from "@mui/material/Link";
import Popover from "@mui/material/Popover";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import CancelIcon from "@mui/icons-material/Cancel";
import HelpIcon from "@mui/icons-material/Help";
import { VerificationCheck } from "../../types/verification";
import { formatCheckCodeLabel, formatStatusLabel } from "../../utils/verificationLabels";
import { getStatusOutcome } from "../../utils/verificationStatus";

export interface ChecksListProps {
  checks: VerificationCheck[];
  /** How many check chips to show inline before the rest go behind "show more...". */
  maxVisible?: number;
}

/** Most verifications run four or fewer checks, so they fit inline as chips. */
const DEFAULT_MAX_VISIBLE_CHECKS = 4;

// One chip per check: a tick for a pass, a cross for a fail, and a neutral
// glyph for anything still undecided (e.g. "PENDING").
function CheckChip({ check }: { check: VerificationCheck }) {
  const { t } = useTranslation();
  const outcome = getStatusOutcome(check.status);
  const icon =
    outcome === "pass" ? <CheckCircleIcon /> : outcome === "fail" ? <CancelIcon /> : <HelpIcon />;

  return (
    <Chip
      size="small"
      icon={icon}
      label={t(formatCheckCodeLabel(check.checkCode))}
      color={outcome === "pass" ? "success" : outcome === "fail" ? "error" : "default"}
      // The icon carries the outcome visually; spell it out for screen readers,
      // which would otherwise hear the check name alone.
      aria-label={`${t(formatCheckCodeLabel(check.checkCode))}: ${t(formatStatusLabel(check.status))}`}
    />
  );
}

// Shows the first `maxVisible` checks inline; any remainder opens in a popover
// anchored to "show more...", so a verification with many checks can't stretch
// the grid row — the same overflow treatment DeclineReasonsList uses.
function ChecksList({ checks, maxVisible = DEFAULT_MAX_VISIBLE_CHECKS }: ChecksListProps) {
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const open = Boolean(anchorEl);
  const { t } = useTranslation();

  if (!checks || checks.length === 0) {
    return <>—</>;
  }

  const visible = checks.slice(0, maxVisible);
  const overflow = checks.slice(maxVisible);

  const handleOpen = (event: MouseEvent<HTMLElement>) => {
    // Don't let this bubble to the grid's row-click handler, which would
    // navigate to the detail page instead of opening the popover.
    event.stopPropagation();
    setAnchorEl(event.currentTarget);
  };
  const handleClose = () => setAnchorEl(null);

  return (
    <Stack
      direction="row"
      alignItems="center"
      spacing={0.75}
      sx={{ minWidth: 0 }}
      onClick={(event) => event.stopPropagation()}
    >
      {visible.map((check) => (
        <CheckChip key={check.checkCode} check={check} />
      ))}
      {overflow.length > 0 && (
        <>
          <Link
            component="button"
            type="button"
            variant="body2"
            underline="always"
            onClick={handleOpen}
            sx={{
              all: "unset",
              flexShrink: 0,
              fontSize: "inherit",
              textDecoration: "underline",
              cursor: "pointer",
            }}
          >
            {t("show more...")}
          </Link>
          <Popover
            open={open}
            anchorEl={anchorEl}
            onClose={handleClose}
            anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
            transformOrigin={{ vertical: "top", horizontal: "left" }}
          >
            <Stack spacing={0.75} alignItems="flex-start" sx={{ p: 1.5, minWidth: 180 }}>
              {overflow.map((check) => (
                <CheckChip key={check.checkCode} check={check} />
              ))}
            </Stack>
          </Popover>
        </>
      )}
    </Stack>
  );
}

export default ChecksList;
